






































































import {
    defineComponent,
    ref,
    computed,
    onMounted,
    Ref,
    useRoute,
    useRouter
} from "@nuxtjs/composition-api";
import { useFAQ } from "~/modules/pageExten/composables/useFAQ";
import type {
    FaqQuestion,
    FaqResult,
    FaqGroupResult,
    UseFAQErrors,
} from "~/modules/pageExten/composables/useFAQ/useFAQ";
import { magento } from "~/config/extensionsApi";
import FaqSearch from '~/modules/pageExten/components/FaqSearch.vue';

export default defineComponent({
    name: "FaqDetailPage",
    components: {
        FaqSearch
    },
    setup() {
        const isDetailActive = ref(false);
        const toggleActive = () => {
          isDetailActive.value = !isDetailActive.value;
        };

        const route = useRoute();
        const router = useRouter();
        
        const {
            questions,
            currentFaqGroup,
            loading,
            error,
            fetchQuestion,
            getFaqGroupById,
            searchQuestions
        } = useFAQ();

        const searchTerm = ref<string>("");
        const activeQuestions = ref<Record<string, boolean>>({});
        const expandedQuestions = ref<Record<string, boolean>>({});
        const groupName = ref<string>("");
        const searchResults = ref<FaqQuestion[]>([]);
        const isSearching = ref<boolean>(false);
        const originalGroupName = ref<string>("");
        const activeTocItem = ref<string>("");

        const mediaUrl = (url) => {
            return `${magento.media_url}/${url}`;
        };

        const questionsList = computed((): FaqQuestion[] => {
            if (!questions.value || !questions.value.items) {
                return [];
            }
            return questions.value.items;
        });

        const filteredQuestionsList = computed((): FaqQuestion[] => {
            if (isSearching.value) {
                return searchResults.value;
            }
            
            if (!searchTerm.value.trim()) {
                return questionsList.value;
            }
            
            const term = searchTerm.value.toLowerCase().trim();
            return questionsList.value.filter(question => 
                question.title.toLowerCase().includes(term) || 
                question.content.toLowerCase().includes(term)
            );
        });

        const hasNoQuestions = computed((): boolean => {
            return filteredQuestionsList.value.length === 0;
        });

        const toggleQuestion = (id: string | number): void => {
            const stringId = String(id);
            
            // Toggle the question's expanded state
            expandedQuestions.value = {
                ...expandedQuestions.value,
                [stringId]: !expandedQuestions.value[stringId]
            };
            
            // Toggle the question's active state in the TOC
            activeTocItem.value = (activeTocItem.value === stringId) ? "" : stringId;
            
            // Set this as active in TOC
            activeTocItem.value = stringId;
            
            // Scroll to element
            setTimeout(() => {
                scrollToElement(`faq-item-${stringId}`);
            }, 100);
        };
        
        // Function to scroll to the top of the page
        const scrollToTop = (): void => {
            // Scroll main window to top smoothly
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            
            // Find and reset all scrollable elements with smooth behavior
            const scrollableElements = document.querySelectorAll('.faq-list__more-content, .faq-item__content, .faq-list__content');
            scrollableElements.forEach(element => {
                if (element instanceof HTMLElement) {
                    element.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    });
                }
            });
            
            // Remove active class from all TOC items
            const tocItems = document.querySelectorAll('.li-item-title');
            tocItems.forEach(item => {
                item.classList.remove('active');
            });
            
            // Reset active TOC item reference
            activeTocItem.value = '';
        };

        // Function to scroll to a specific element by ID
        const scrollToElement = (elementId: string): void => {
            const element = document.getElementById(elementId);
            if (element) {
                element.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
            }
        };

        // New function to scroll to a question and open it
        const scrollToQuestionAndOpen = (id: string | number): void => {
            const stringId = String(id);
            const elementId = `faq-item-${stringId}`;
            
            // Set this item as active in TOC
            activeTocItem.value = stringId;
            
            // Scroll to it after a small delay
            setTimeout(() => {
                scrollToElement(elementId);
            }, 50);
            
            // Toggle the active state after scrolling
            if (isDetailActive.value) {
                isDetailActive.value = false;
            }
        };

        const handleSearch = async (): Promise<void> => {
            const term = searchTerm.value.trim();
            
            if (term !== "") {
                // Redirect to the search page with search term
                router.push({
                    path: '/hoi-dap/tim-kiem',
                    query: { q: term }
                });
            } else {
                // Clear search results if term is empty
                isSearching.value = false;
                searchResults.value = [];
                
                // Restore original group name if we have one
                if (originalGroupName.value) {
                    groupName.value = originalGroupName.value;
                }
            }
        };

        onMounted(async () => {
            const faqGroupId = route.value.params.faq_group_id;
            const searchQuery = route.value.query.q as string;
            
            // Check if this is a search request
            if (searchQuery) {
                // Set search term from URL
                searchTerm.value = searchQuery;
                groupName.value = `Kết quả tìm kiếm: "${searchQuery}"`;
                
                // Perform search automatically
                isSearching.value = true;
                
                // Direct API search with proper URL encoding
                let searchCriteria = {
                    'searchCriteria[filter_groups][0][filters][0][field]': 'status',
                    'searchCriteria[filter_groups][0][filters][0][value]': '1',
                    'searchCriteria[filter_groups][0][filters][0][condition_type]': 'eq',
                    'searchCriteria[filter_groups][1][filters][0][field]': 'title',
                    'searchCriteria[filter_groups][1][filters][0][value]': '%' + searchQuery + '%',
                    'searchCriteria[filter_groups][1][filters][0][condition_type]': 'like',
                };
                
                const result = await searchQuestions(searchQuery, searchCriteria);
                if (result && result.items) {
                    searchResults.value = result.items;
                } else {
                    searchResults.value = [];
                }
            }
            // If not a search request but a regular group page
            else if (faqGroupId) {
                // Fetch the FAQ group details
                const groupData = await getFaqGroupById(faqGroupId);
                if (groupData) {
                    groupName.value = groupData.group_name;
                    originalGroupName.value = groupData.group_name;
                }
                
                // Fetch questions for this group
                const questionSearchCriteria = {
                    'searchCriteria[filter_groups][0][filters][0][field]': 'group',
                    'searchCriteria[filter_groups][0][filters][0][value]': faqGroupId,
                    'searchCriteria[filter_groups][0][filters][0][condition_type]': 'eq',
                    'searchCriteria[filter_groups][0][filters][1][field]': 'status',
                    'searchCriteria[filter_groups][0][filters][1][value]': '1',
                    'searchCriteria[filter_groups][0][filters][1][condition_type]': 'eq',
                };
                
                await fetchQuestion(questionSearchCriteria);
            }

            // Handle hash changes for scrolling to specific FAQ items
            if (route.value.hash) {
                const hash = route.value.hash.substring(1); // Remove the # character
                setTimeout(() => {
                    const element = document.getElementById(hash);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        
                        // If this is a question, automatically open it
                        if (hash.startsWith('faq-item-')) {
                            const questionId = hash.replace('faq-item-', '');
                            activeQuestions.value = {
                                ...activeQuestions.value,
                                [questionId]: true
                            };
                        }
                    }
                }, 300); // Short delay to ensure DOM is ready
            }
            
            // Add event listener for anchor links within the page
            document.addEventListener('click', (event) => {
                const target = event.target as HTMLElement;
                if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#faq-item-')) {
                    event.preventDefault();
                    const hash = target.getAttribute('href')?.substring(1);
                    if (hash) {
                        scrollToElement(hash);
                        
                        // If this is a question, automatically open it
                        if (hash.startsWith('faq-item-')) {
                            const questionId = hash.replace('faq-item-', '');
                            activeQuestions.value = {
                                ...activeQuestions.value,
                                [questionId]: true
                            };
                        }
                    }
                }
            });
        });

        return {
            searchTerm,
            loading,
            error,
            questionsList,
            questions,
            filteredQuestionsList,
            hasNoQuestions,
            toggleQuestion,
            handleSearch,
            activeQuestions,
            expandedQuestions,
            groupName,
            mediaUrl,
            originalGroupName,
            toggleActive,
            isDetailActive,
            scrollToTop,
            scrollToElement,
            scrollToQuestionAndOpen,
            activeTocItem
        };
    },
});
